import { api } from "../api-client.js";
import { renderEnvTabs, type Env } from "../components/env-tabs.js";
import { renderEmptyStateBanner, type ScreenState } from "../components/empty-state-banner.js";

type TodayPnlSource = "DAILY_SUMMARY" | "LIVE_TRADES" | "NONE";

interface DashboardSummary {
  env: Env;
  state: ScreenState;
  today_pnl: number | null;
  today_pnl_source: TodayPnlSource;
  today_return_pct: number | null;
  today_pnl_as_of_date: string | null;
  cumulative_return_pct: number | null;
  cumulative_as_of_date: string | null;
  last_synced_at: string | null;
}

function fmtMoney(v: number | null): string {
  if (v === null) return "—";
  return `${v.toLocaleString("ko-KR")}원`;
}

function fmtPct(v: number | null): string {
  if (v === null) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
}

function valueClass(v: number | null): string {
  if (v === null) return "value-neutral";
  return v > 0 ? "value-profit" : v < 0 ? "value-loss" : "value-neutral";
}

function fmtSyncedAt(iso: string | null): string {
  if (!iso) return "동기화 이력 없음";
  return new Date(iso).toLocaleString("ko-KR");
}

function fmtDateShort(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  return `${Number(m)}/${Number(d)}`;
}

// 002 §7 화면2(대시보드): 요약 카드 + env 전환 탭(기본 운영). Phase1엔 미니 차트는 생략(수익률
// 화면에서 전체 차트를 제공하므로 중복 구현하지 않음).
export async function renderDashboard(container: HTMLElement): Promise<void> {
  let currentEnv: Env = "운영";

  // env 탭을 바꿀 때마다 화면 전체를 지웠다가 다시 그리면 응답을 기다리는 동안 깜빡였다 — 제목/탭은
  // 슬롯에 담아 한 번만 배치하고, 결과 영역은 새 내용이 다 준비된 뒤 한 번에 교체한다.
  container.innerHTML = `<h2 class="page-title">대시보드</h2>`;
  const tabsSlot = document.createElement("div");
  const resultsSlot = document.createElement("div");
  container.append(tabsSlot, resultsSlot);

  async function load(env: Env): Promise<void> {
    tabsSlot.replaceChildren(
      renderEnvTabs(env, (newEnv) => {
        currentEnv = newEnv;
        void load(currentEnv);
      }),
    );

    let summary: DashboardSummary;
    try {
      summary = await api.get<DashboardSummary>(`/api/dashboard/summary?env=${encodeURIComponent(env)}`);
    } catch {
      const err = document.createElement("p");
      err.textContent = "데이터를 불러오지 못했습니다.";
      resultsSlot.replaceChildren(err);
      return;
    }

    const frag = document.createDocumentFragment();

    const banner = renderEmptyStateBanner(summary.state, env, (newEnv) => {
      currentEnv = newEnv;
      void load(currentEnv);
    });
    if (banner) frag.appendChild(banner);

    // Core의 공식 DAILY_SUMMARY가 아직 오늘자로 안 왔으면, 오늘 체결(trade_events)로 직접 집계한
    // 잠정치를 보여준다 — 값 자체는 "오늘 것"이 맞으므로 라벨은 그대로 "오늘"을 쓰고, 잠정치임만 안내한다.
    if (summary.state === "OK" && summary.today_pnl_source === "LIVE_TRADES") {
      const liveBanner = document.createElement("div");
      liveBanner.className = "banner banner-info";
      liveBanner.innerHTML = `<span>오늘 손익은 체결 내역 기준 <strong>실시간 잠정치</strong>입니다 — Core의 공식 집계가 도착하면 값이 갱신됩니다.</span>`;
      frag.appendChild(liveBanner);
    }

    const grid = document.createElement("div");
    grid.className = "card-grid";

    // 사용자 요청: 오늘이 주말/공휴일이면(today_pnl_as_of_date가 채워짐) "오늘"이 아니라 가장
    // 최근 거래일 값을 보여주는 것이므로, 누적 수익률 카드와 같은 방식으로 기준일을 라벨에 붙인다.
    const asOfDate = summary.today_pnl_as_of_date;
    const todayPnlLabel =
      summary.today_pnl_source === "LIVE_TRADES"
        ? "오늘 손익 (잠정)"
        : asOfDate
          ? `최근 손익 (${fmtDateShort(asOfDate)} 기준)`
          : "오늘 손익";
    const todayReturnLabel = asOfDate ? `최근 수익률 (${fmtDateShort(asOfDate)} 기준)` : "오늘 수익률";

    const cards: Array<{ label: string; value: string; cls: string }> = [
      { label: todayPnlLabel, value: fmtMoney(summary.today_pnl), cls: valueClass(summary.today_pnl) },
      {
        label: todayReturnLabel,
        value: fmtPct(summary.today_return_pct),
        cls: valueClass(summary.today_return_pct),
      },
      {
        label: summary.cumulative_as_of_date
          ? `누적 수익률 (${fmtDateShort(summary.cumulative_as_of_date)} 기준)`
          : "누적 수익률",
        value: fmtPct(summary.cumulative_return_pct),
        cls: valueClass(summary.cumulative_return_pct),
      },
    ];

    for (const c of cards) {
      const card = document.createElement("div");
      card.className = "card";
      card.innerHTML = `
        <div class="stat-label">${c.label}</div>
        <div class="stat-value ${c.cls}">${c.value}</div>
      `;
      grid.appendChild(card);
    }
    frag.appendChild(grid);

    const syncedCard = document.createElement("div");
    syncedCard.className = "card";
    syncedCard.innerHTML = `<div class="stat-label">마지막 동기화</div><div class="stat-value" style="font-size:16px">${fmtSyncedAt(summary.last_synced_at)}</div>`;
    frag.appendChild(syncedCard);

    resultsSlot.replaceChildren(frag);
  }

  await load(currentEnv);
}
